import L from 'leaflet';

const MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
  <path d="M21 0C29.8364 0 36.9998 7.15991 37 15.9922C37 21.1483 32.9296 27.8417 25.0205 36.0127L23.7393 37.3154C23.302 37.7535 22.8539 38.1954 22.3955 38.6416L21 40L18.9248 37.9756C18.7009 37.7545 18.4794 37.5345 18.2607 37.3154L16.9795 36.0127C9.0704 27.8417 5 21.1483 5 15.9922C5.00022 7.15991 12.1636 0 21 0ZM21 8.49316C16.8579 8.49316 13.5 11.851 13.5 15.9932C13.5002 20.1351 16.858 23.4932 21 23.4932C25.142 23.4931 28.4998 20.1351 28.5 15.9932C28.5 11.851 25.1421 8.49319 21 8.49316Z" fill="#85654D"/>
</svg>`;

const MOBILE_BREAKPOINT = 991.98;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildMarkerIcon(popupAnchor = [0, -42]) {
  return L.divIcon({
    html: MARKER_SVG,
    className: 'partner-locator-map-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor,
  });
}

const POPUP_ANCHOR = [0, -42];

function getCssVar(name, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export function initPartnerLocator(root = document) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const headerHeight = parseFloat(getCssVar('--site-header-height', '5.5rem')) || 88;
  const pinnedMapHeight = getCssVar('--partner-locator-map-pinned-h', '16rem');

  const locators = root.querySelectorAll('.js-partner-locator');

  locators.forEach((locator) => {
    if (locator.dataset.initialized === 'true') {
      return;
    }
    locator.dataset.initialized = 'true';

    const mapElement = locator.querySelector('.js-partner-locator-map');
    const dataElement = locator.querySelector('.js-partner-locator-data');
    const searchInput = locator.querySelector('.js-partner-locator-search');
    const emptyState = locator.querySelector('.js-partner-locator-empty');
    const listElement = locator.querySelector('.js-partner-locator-list');
    const itemElements = Array.from(locator.querySelectorAll('[data-partner-locator-item]'));
    const mapColumn = locator.querySelector('.partner-locator__map-column');
    let applyFilterHandler = null;

    // ---- Filter panel state + elements ----
    const filterToggle = locator.querySelector('.js-partner-locator-filter-toggle');
    const filterPanel = locator.querySelector('.js-partner-locator-filter-panel');
    const filterClose = locator.querySelector('.js-partner-locator-filter-close');
    const filterForm = locator.querySelector('.js-partner-locator-filter-form');
    const filterAccordions = Array.from(locator.querySelectorAll('.js-partner-locator-filter-accordion'));
    const filterCheckboxes = Array.from(locator.querySelectorAll('.partner-locator__filter-checkbox'));
    const filterApply = locator.querySelector('.js-partner-locator-filter-apply');
    const filterReset = locator.querySelector('.js-partner-locator-filter-reset');
    const filterCount = locator.querySelector('.js-partner-locator-filter-count');
    const filterBackdrop = locator.querySelector('.js-partner-locator-filter-backdrop');

    // Currently-selected filter values (populated on Apply).
    let selectedTerritories = [];
    let selectedRete = [];

    const hasFilters = filterToggle && filterPanel;

    let mapItems = [];

    if (dataElement?.textContent) {
      try {
        mapItems = JSON.parse(dataElement.textContent);
      } catch (error) {
        console.error('Unable to parse partner locator map data.', error);
      }
    }

    const itemsById = new Map(
      itemElements.map((item) => [item.dataset.partnerId, item])
    );

    const isMobile = () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;

    const highlightItem = (id, { scrollIntoView = false } = {}) => {
      itemElements.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.partnerId === String(id));
      });

      const activeItem = itemsById.get(String(id));
      if (scrollIntoView && activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    const matchesFilterGroup = (selected, attrValue) => {
      if (selected.length === 0) {
        return true;
      }

      const values = (attrValue || '').split(' ').filter(Boolean);
      return selected.some((value) => values.includes(value));
    };

    const applyFilter = (markersById = null, map = null) => {
      const query = searchInput?.value.trim().toLowerCase() || '';
      const visibleIds = [];

      itemElements.forEach((item) => {
        const haystack = (item.dataset.searchContent || '').toLowerCase();
        const textMatch = query === '' || haystack.includes(query);

        const territoryMatch = hasFilters
          ? matchesFilterGroup(selectedTerritories, item.dataset.territory)
          : true;
        const reteMatch = hasFilters
          ? matchesFilterGroup(selectedRete, item.dataset.partnerTag)
          : true;

        const matches = textMatch && territoryMatch && reteMatch;

        item.hidden = !matches;
        item.classList.toggle('is-hidden', !matches);
        if (!matches) {
          item.classList.remove('is-active');
        }

        if (matches) {
          visibleIds.push(item.dataset.partnerId);
        }
      });

      if (emptyState) {
        emptyState.hidden = visibleIds.length > 0;
      }

      if (!markersById || !map) {
        return visibleIds;
      }

      const visibleMarkers = [];

      markersById.forEach((marker, id) => {
        const isVisible = visibleIds.includes(String(id));

        if (isVisible) {
          if (!map.hasLayer(marker)) {
            marker.addTo(map);
          }
          visibleMarkers.push(marker);
        } else {
          if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        }
      });

      if (!visibleMarkers.length) {
        return visibleIds;
      }

      if (visibleMarkers.length === 1) {
        map.setView(visibleMarkers[0].getLatLng(), 14);
        return visibleIds;
      }

      const group = L.featureGroup(visibleMarkers);
      map.fitBounds(group.getBounds(), { maxZoom: 14, padding: [20, 20] });

      return visibleIds;
    };

    // Map + markers (may stay null if no map element or no coordinates).
    let map = null;
    let markersById = null;

    if (mapElement && mapItems.length) {
      try {
        const defaultCenter = [mapItems[0].lat, mapItems[0].lng];

        map = L.map(mapElement, {
          center: defaultCenter,
          zoom: 14,
          zoomControl: true,
          scrollWheelZoom: false,
        });

        mapElement.addEventListener('mouseenter', () => map.scrollWheelZoom.enable());
        mapElement.addEventListener('mouseleave', () => map.scrollWheelZoom.disable());

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(map);

        let markerIcon = buildMarkerIcon(POPUP_ANCHOR);
        markersById = new Map();

        mapItems.forEach((item) => {
          const marker = L.marker([item.lat, item.lng], { icon: markerIcon });

          const popupContent = `
            <div class="partner-locator-map-info-window">
              <strong>${escapeHtml(item.title)}</strong>
              ${item.description ? `<p>${escapeHtml(item.description)}</p>` : ''}
              <a href="${escapeHtml(item.url)}">${escapeHtml('Scopri di più')}</a>
            </div>
          `;

          marker.bindPopup(popupContent, { maxWidth: 256, className: 'partner-locator-popup' });

          marker.on('click', () => {
            highlightItem(item.id, { scrollIntoView: true });
            if (isMobile()) {
              enterFocusMode(item.id);
            }
          });

          marker.addTo(map);
          markersById.set(String(item.id), marker);
        });

        // Fit map to all markers on first load.
        if (mapItems.length > 1) {
          const group = L.featureGroup(Array.from(markersById.values()));
          map.fitBounds(group.getBounds(), { maxZoom: 14, padding: [20, 20] });
        }

        // Clicking a list item pans the map to its marker.
        itemElements.forEach((item) => {
          item.addEventListener('click', (event) => {
            if (event.target.closest('a')) {
              return;
            }
            if (event.target.closest('.partner-locator-item__close')) {
              return;
            }

            const marker = markersById.get(item.dataset.partnerId);
            if (marker && map) {
              map.flyTo(marker.getLatLng(), 14, { duration: 0.6 });
              marker.openPopup();
            }

            highlightItem(item.dataset.partnerId);

            if (isMobile()) {
              enterFocusMode(item.dataset.partnerId);
            }
          });
        });
      } catch (error) {
        console.error('Partner locator map initialization failed.', error);
      }
    }

    // ---------------------------------------------------------------------------
    // Mobile enhancements: focus mode + sticky map/search
    // ---------------------------------------------------------------------------
    let stickyObserver = null;

    const invalidateMap = () => {
      if (!map) return;
      requestAnimationFrame(() => map.invalidateSize({ animate: false }));
    };

    const onMapTransitionEnd = () => {
      if (!map) return;
      mapElement.addEventListener('transitionend', invalidateMap, { once: true });
    };

    // Focus mode: show only the selected partner in the list + map,
    // and fly the map to that partner's marker.
    const enterFocusMode = (id) => {
      locator.classList.add('is-focus-mode');

      itemElements.forEach((item) => {
        if (item.dataset.partnerId === String(id)) {
          item.hidden = false;
          item.classList.remove('is-hidden');
          item.classList.add('is-active');
        } else {
          item.hidden = true;
          item.classList.add('is-hidden');
          item.classList.remove('is-active');
        }
      });

      if (emptyState) {
        emptyState.hidden = true;
      }

      if (markersById && map) {
        const selectedMarker = markersById.get(String(id));

        markersById.forEach((marker, mid) => {
          if (String(mid) === String(id)) {
            if (!map.hasLayer(marker)) marker.addTo(map);
          } else if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        });

        if (selectedMarker) {
          map.flyTo(selectedMarker.getLatLng(), 15, { duration: 0.6 });
        }
      }
    };

    // Exit focus mode: restore all partners and fit map to all markers.
    const exitFocusMode = () => {
      if (!locator.classList.contains('is-focus-mode')) return;

      locator.classList.remove('is-focus-mode');

      itemElements.forEach((item) => {
        item.hidden = false;
        item.classList.remove('is-hidden', 'is-active');
      });

      if (markersById && map) {
        map.closePopup();

        markersById.forEach((marker) => {
          if (!map.hasLayer(marker)) marker.addTo(map);
        });

        const allMarkers = Array.from(markersById.values());
        if (allMarkers.length > 1) {
          const group = L.featureGroup(allMarkers);
          map.fitBounds(group.getBounds(), { maxZoom: 14, padding: [20, 20] });
        }
      }

      applyFilter(markersById, map);
    };

    // X close button (delegated on the list).
    if (listElement) {
      listElement.addEventListener('click', (event) => {
        const closeBtn = event.target.closest('.partner-locator-item__close');
        if (closeBtn) {
          exitFocusMode();
        }
      });
    }

    // Sticky map: detect when the map column hits the header, then shrink it.
    const setupSticky = () => {
      if (!mapColumn) return;

      stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const pinned = !entry.isIntersecting;
          mapColumn.classList.toggle('is-pinned', pinned);
          locator.style.setProperty(
            '--partner-locator-mobile-map-h',
            pinned ? pinnedMapHeight : '25rem'
          );

          if (reduceMotion) {
            invalidateMap();
          } else {
            onMapTransitionEnd();
          }
        },
        { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 }
      );

      stickyObserver.observe(mapColumn);
    };

    const teardownSticky = () => {
      if (stickyObserver) {
        stickyObserver.disconnect();
        stickyObserver = null;
      }
      if (mapColumn) {
        mapColumn.classList.remove('is-pinned');
      }
      locator.style.removeProperty('--partner-locator-mobile-map-h');
      invalidateMap();
    };

    // Search: exit focus mode first, then filter.
    applyFilterHandler = () => {
      if (locator.classList.contains('is-focus-mode')) {
        exitFocusMode();
      }
      applyFilter(markersById, map);
    };

    if (searchInput) {
      searchInput.addEventListener('input', applyFilterHandler);
    }

    // ---- Filter panel wiring ----
    const updateFilterCount = () => {
      const count = selectedTerritories.length + selectedRete.length;

      if (filterCount) {
        filterCount.hidden = count < 1;
        filterCount.textContent = String(count);
      }

      if (filterReset) {
        filterReset.hidden = count < 1;
      }
    };

    const openFilterPanel = () => {
      if (!filterPanel || !filterToggle) {
        return;
      }

      filterPanel.classList.add('is-open');
      filterPanel.setAttribute('aria-hidden', 'false');
      filterToggle.setAttribute('aria-expanded', 'true');

      if (filterBackdrop) {
        filterBackdrop.classList.add('is-open');
      }
    };

    const closeFilterPanel = ({ focusToggle = true } = {}) => {
      if (!filterPanel || !filterToggle) {
        return;
      }

      filterPanel.classList.remove('is-open');
      filterPanel.setAttribute('aria-hidden', 'true');
      filterToggle.setAttribute('aria-expanded', 'false');

      if (filterBackdrop) {
        filterBackdrop.classList.remove('is-open');
      }

      if (focusToggle) {
        filterToggle.focus();
      }
    };

    const collectSelectedFilters = () => {
      const territories = [];
      const rete = [];

      filterCheckboxes.forEach((checkbox) => {
        if (!checkbox.checked) {
          return;
        }

        const group = checkbox.closest('.js-partner-locator-filter-accordion');

        if (group && group.dataset.filterGroup === 'territory') {
          territories.push(checkbox.value);
        } else if (group && group.dataset.filterGroup === 'rete') {
          rete.push(checkbox.value);
        }
      });

      return { territories, rete };
    };

    const applySelectedFilters = (closePanel = true) => {
      const { territories, rete } = collectSelectedFilters();

      selectedTerritories = territories;
      selectedRete = rete;

      updateFilterCount();

      if (closePanel) {
        closeFilterPanel();
      }

      if (locator.classList.contains('is-focus-mode')) {
        exitFocusMode();
      } else {
        applyFilter(markersById, map);
      }
    };

    if (filterToggle) {
      filterToggle.addEventListener('click', () => {
        const isOpen = filterPanel.classList.contains('is-open');

        if (isOpen) {
          closeFilterPanel();
        } else {
          openFilterPanel();
        }
      });
    }

    if (filterClose) {
      filterClose.addEventListener('click', () => closeFilterPanel());
    }

    // Tapping the backdrop (outside the panel) closes the filter panel.
    if (filterBackdrop) {
      filterBackdrop.addEventListener('click', () => closeFilterPanel());
    }

    // Accordion toggles (open one group at a time, mirroring filtered-grid).
    filterAccordions.forEach((accordion) => {
      const toggleBtn = accordion.querySelector('.js-partner-locator-filter-accordion-toggle');

      if (!toggleBtn) {
        return;
      }

      toggleBtn.addEventListener('click', () => {
        const isOpen = accordion.classList.contains('is-open');
        const body = accordion.querySelector('.js-partner-locator-filter-accordion-body');

        // Close every other accordion first.
        filterAccordions.forEach((other) => {
          if (other === accordion) {
            return;
          }

          other.classList.remove('is-open');
          const otherBtn = other.querySelector('.js-partner-locator-filter-accordion-toggle');
          const otherBody = other.querySelector('.js-partner-locator-filter-accordion-body');

          if (otherBtn) {
            otherBtn.setAttribute('aria-expanded', 'false');
          }

          if (otherBody) {
            otherBody.hidden = true;
          }
        });

        accordion.classList.toggle('is-open', !isOpen);
        toggleBtn.setAttribute('aria-expanded', String(!isOpen));

        if (body) {
          body.hidden = isOpen;
        }
      });
    });

    // Apply button (form submit).
    if (filterForm) {
      filterForm.addEventListener('submit', (event) => {
        event.preventDefault();
        applySelectedFilters(true);
      });
    }

    // Reset button: clear all checkboxes, re-apply (which clears filter state).
    if (filterReset) {
      filterReset.addEventListener('click', () => {
        filterCheckboxes.forEach((checkbox) => {
          checkbox.checked = false;
        });

        filterAccordions.forEach((accordion) => {
          accordion.classList.remove('is-open');
          const btn = accordion.querySelector('.js-partner-locator-filter-accordion-toggle');
          const body = accordion.querySelector('.js-partner-locator-filter-accordion-body');

          if (btn) {
            btn.setAttribute('aria-expanded', 'false');
          }

          if (body) {
            body.hidden = true;
          }
        });

        applySelectedFilters(false);
      });
    }

    // Close the panel on Escape for accessibility.
    if (filterPanel) {
      filterPanel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          closeFilterPanel();
        }
      });
    }

    applyFilter(markersById, map);

    // Reposition popups: keep every marker's icon using the single anchor
    // (above the pin) for all viewports, restoring the original behavior.
    const refreshMarkerIcons = () => {
      if (!markersById) return;

      const hadOpenPopup = map && map._popup && map._popup.isOpen();

      markerIcon = buildMarkerIcon(POPUP_ANCHOR);
      markersById.forEach((marker) => {
        marker.setIcon(markerIcon);
      });

      // Re-open the popup on the marker that had it, so it repositions.
      if (hadOpenPopup && markersById.size === 1) {
        const onlyMarker = Array.from(markersById.values())[0];
        onlyMarker.openPopup();
      }
    };

    // Resize: toggle mobile behavior when crossing the breakpoint.
    let mobileActive = isMobile();

    const handleResize = () => {
      const nowMobile = isMobile();

      if (nowMobile === mobileActive) return;
      mobileActive = nowMobile;

      if (nowMobile) {
        setupSticky();
      } else {
        teardownSticky();
        exitFocusMode();
      }

      refreshMarkerIcons();
    };

    window.addEventListener('resize', handleResize);

    if (mobileActive) {
      setupSticky();
    }
  });
}
